import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, Autocomplete, Stack, Typography, Box, Button, DialogActions } from '@mui/material';
import { postFacilityBookingAccountForm } from '../../../../api/facility';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { AwaitButton } from '../../../../reusable-components/await-button';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import SimpleAutocompleteProps from '../../../../reusable-components/autocomplete/SimpleAutocompleteProps';
import BookingAccountSearch from './BookingAccountSearch';
import { bookingTypeIsVendorAccountSeachable } from './BookingAccountSearchHelper';

BookingAccountForm.propTypes = {
  fetchData: PropTypes.func,
  editRow: PropTypes.object,
  facilityRow: PropTypes.object,
  vendors: PropTypes.array,
  vendorBookingTypes: PropTypes.array,
  openForm: PropTypes.bool,
  close: PropTypes.func,
  bookingType: PropTypes.object
};

const yesNoOptions = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

export default function BookingAccountForm({ fetchData, vendors, vendorBookingTypes, editRow, facilityRow, openForm, close, bookingType }) {
  const { enqueueSnackbar } = useSnackbar();

  const [value, setValue] = useState({ vendorId: '', bookingId: '', addToAllFacilities: null });

  useEffect(() => {
    setValue({ vendorId: editRow?.vendorId, bookingId: editRow?.vendorBookingAccountId, addToAllFacilities: null });
  }, [editRow, openForm])

  const handleSave = async () => {
    const response = await postFacilityBookingAccountForm({ ...value, facilityId: facilityRow?.id, id: editRow?.id });
    if (response.data?.message) {
      enqueueSnackbar(response.data?.message, {
        variant: response.data?.message === 'Account Added' || response.data?.message === 'Account Updated' ? 'success' : 'error',
      });
    } else enqueueSnackbar('Failed to add booking account', { variant: 'error' });
    fetchData();
    close();
  };

  const bookingAcounts = (vendorBookingTypes?.filter((type) => type.vendorId === value?.vendorId)
    .map((type) => ({ value: type.id, label: type.subaccount ? `${type.account} - ${type.subaccount}` : type.account })) ?? []);

  return <Dialog open={openForm} onClose={(event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    close();
  }} maxWidth="md" fullWidth>
    <DialogTitle sx={{ textAlign: 'center' }} >
      {editRow ? 'Edit' : 'Add'} Booking Account
      <CloseIconButton onClick={close} />
    </DialogTitle>
    <DialogContent sx={{ height: '100%', width: '100%' }} dividers>
      <Box sx={{ width: '100%' }}>
        <Stack spacing={1}>
          <Box>
            <Typography variant="subtitle1">
              Vendor
            </Typography>
            <Autocomplete
              {...SimpleAutocompleteProps}
              value={vendors.find(vendor => vendor.value === value.vendorId) ?? null}
              onChange={(event, newValue) => setValue(value => ({ ...value, vendorId: newValue?.value ?? "", bookingId: '' }))}
              options={vendors ?? []}            
            />
          </Box>       
          <Box>
            <Typography variant="subtitle1">
              Booking Vendor Account
            </Typography>
            <Autocomplete
              {...SimpleAutocompleteProps}
              onChange={(event, newValue) => setValue({ ...value, bookingId: newValue?.value ?? "" })}
              getOptionKey={(value) => value.value}
              value={bookingAcounts.find(account => account.value === value.bookingId) ?? null}
              options={bookingAcounts}
            />
          </Box>
          {bookingTypeIsVendorAccountSeachable(bookingType) && <BookingAccountSearch bookingType={bookingType} />}         
          <Box>
            <Typography variant="subtitle1">
              Add To All Facilities
            </Typography>
            <Autocomplete
              {...SimpleAutocompleteProps}
              value={yesNoOptions.find(option => option.value === value?.addToAllFacilities) ?? null}
              onChange={(event, newValue) => setValue({ ...value, addToAllFacilities: newValue?.value ?? null })}
              options={yesNoOptions}
            />
          </Box>
        </Stack>
      </Box>
    </DialogContent>
    <DialogActions>
      <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={close}>
        Cancel
      </Button>
      <AwaitButton
        variant="contained"
        color="primary"
        size="medium"
        disabled={!value.vendorId || !value.bookingId || value.addToAllFacilities === null}
        sx={{ width: 150 }}
        onClick={handleSave}
      >
        {editRow ? 'Save' : 'Add Account'}
      </AwaitButton>
    </DialogActions>
  </Dialog>;
}
